import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, ShoppingBag, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { API, useBasket, resolveImageUrl } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BasketPage = () => {
  const { items, removeItem, clearBasket } = useBasket();
  const [step, setStep] = useState("basket"); // basket, form, success
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    other_country: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({ ...prev, country: value, other_country: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;

    setLoading(true);
    try {
      await axios.post(`${API}/requests/batch`, {
        products: items.map(item => ({
          product_id: item.id,
          product_name: item.name,
          product_sku: item.sku
        })),
        ...formData
      });
      setStep("success");
      clearBasket();
    } catch (error) {
      console.error("Error submitting request:", error);
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center px-6" data-testid="basket-success">
        <motion.div
          className="text-center max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-20 h-20 mx-auto mb-8 border border-[#C5A059] rounded-full flex items-center justify-center"
          >
            <Check size={40} className="text-[#C5A059]" strokeWidth={1.5} />
          </motion.div>
          <h1 className="font-['Playfair_Display'] text-3xl text-white mb-4">
            Request Submitted
          </h1>
          <p className="text-gray-400 text-sm mb-10">
            Thank you for your interest. We will review your request and contact you shortly with details about the selected products.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-3 border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
            data-testid="continue-shopping-btn"
          >
            Continue Browsing
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="basket-page">
      <section className="px-6 sm:px-12 lg:px-24 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm mb-8"
            data-testid="back-to-products-link"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            Back to Products
          </Link>

          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-4">
                Your Selection
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-light text-white tracking-tight">
                Basket
              </h1>
            </div>
            {items.length > 0 && (
              <p className="text-gray-500 text-sm">
                {items.length} {items.length === 1 ? "item" : "items"}
              </p>
            )}
          </div>
        </motion.div>

        {items.length === 0 && step === "basket" ? (
          <motion.div
            className="text-center py-24"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <ShoppingBag size={48} className="text-gray-700 mx-auto mb-6" strokeWidth={1} />
            <p className="text-gray-500 mb-8">Your basket is empty.</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-3 border border-white/40 rounded-full text-white px-6 py-3 text-sm tracking-widest uppercase font-medium hover:border-white hover:bg-white hover:text-black transition-colors duration-300"
              data-testid="browse-products-btn"
            >
              Browse Products
            </Link>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Items List */}
            <div className="lg:col-span-3 space-y-4">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="bg-[#0A0A0A] border border-white/5 p-4 sm:p-6 flex items-center gap-4 sm:gap-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`basket-item-${item.slug}`}
                >
                  <Link to={`/products/${item.slug}`} className="flex-shrink-0">
                    <div className="w-20 h-20 bg-[#121212]">
                      {item.cover_image ? (
                        <img src={resolveImageUrl(item.cover_image)} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                          No img
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.slug}`} className="hover:text-[#C5A059] transition-colors">
                      <h3 className="text-white truncate">{item.name}</h3>
                    </Link>
                    <p className="text-gray-500 text-sm mt-1">{item.collection || "Collection"}</p>
                    <p className="text-gray-600 text-xs mt-1">{item.sku}</p>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="p-2 text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                    data-testid={`remove-basket-item-${item.slug}`}
                  >
                    <Trash2 size={18} strokeWidth={1.5} />
                  </button>
                </motion.div>
              ))}

              {step === "basket" && (
                <motion.div
                  className="pt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <button
                    onClick={() => setStep("form")}
                    className="w-full sm:w-auto border border-white rounded-full text-white px-10 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300"
                    data-testid="proceed-to-request-btn"
                  >
                    Submit a Request for All Items
                  </button>
                </motion.div>
              )}
            </div>

            {/* Request Form */}
            {step === "form" && (
              <motion.div
                className="lg:col-span-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 sticky top-28">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] mb-2">
                    Product Inquiry
                  </p>
                  <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">
                    Your Details
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="basket-request-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">First Name *</label>
                        <input
                          type="text" name="first_name" value={formData.first_name} onChange={handleChange} required
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                          data-testid="basket-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Last Name *</label>
                        <input
                          type="text" name="last_name" value={formData.last_name} onChange={handleChange} required
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                          data-testid="basket-last-name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Email *</label>
                      <input
                        type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                        data-testid="basket-email"
                      />
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Phone *</label>
                      <input
                        type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                        data-testid="basket-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Country *</label>
                      <Select value={formData.country} onValueChange={handleCountryChange} required>
                        <SelectTrigger
                          className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 text-white text-sm focus:ring-0 focus:border-white h-auto"
                          data-testid="basket-country-trigger"
                        >
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10" style={{ backgroundColor: '#0A0A0A' }}>
                          <SelectItem value="Italy" className="text-white hover:bg-white/10">Italy</SelectItem>
                          <SelectItem value="Switzerland" className="text-white hover:bg-white/10">Switzerland</SelectItem>
                          <SelectItem value="Germany" className="text-white hover:bg-white/10">Germany</SelectItem>
                          <SelectItem value="Russia" className="text-white hover:bg-white/10">Russia</SelectItem>
                          <SelectItem value="Other" className="text-white hover:bg-white/10">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.country === "Other" && (
                      <div>
                        <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Please specify *</label>
                        <input
                          type="text" name="other_country" value={formData.other_country} onChange={handleChange} required
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors"
                          data-testid="basket-other-country"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-gray-500 mb-2">Message *</label>
                      <textarea
                        name="message" value={formData.message} onChange={handleChange} required rows={3}
                        placeholder="Tell us about your project and requirements..."
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white text-sm focus:outline-none focus:border-white transition-colors resize-none placeholder:text-gray-600"
                        data-testid="basket-message"
                      />
                    </div>

                    <button
                      type="submit" disabled={loading}
                      className="w-full border border-white rounded-full text-white px-8 py-4 text-sm tracking-widest uppercase font-medium hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                      data-testid="submit-basket-request-btn"
                    >
                      {loading ? "Submitting..." : "Submit Request"}
                    </button>

                    <button
                      type="button"
                      onClick={() => setStep("basket")}
                      className="w-full text-gray-500 hover:text-white text-sm transition-colors py-2"
                      data-testid="back-to-basket-btn"
                    >
                      Back to basket
                    </button>
                  </form>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default BasketPage;
